import { ButtonStyled, SubmitButtonStyled } from '@OnArk/styles/Buttons.styled';
import { ErrorMsgsStyled } from '@OnArk/styles/Form.styled';

import React, { ReactElement } from 'react';

import { useInternalForm } from '@hangar31/rc-form';
import { useOrderableContext } from '@OnArk/OrderedList';
import Icon from '@OnArk/SVGs/Icon';
import posthog from '@OnArk/utils/posthog';

export default function Footer({
  children,
  closeFn,
  item,
}: React.PropsWithChildren<{
  item?: { id: string };
  closeFn?: () => void;
}>): ReactElement {
  const { removeItem } = useOrderableContext();
  // Every builder modal is an `ItemModalStyled` (a styled rc-form `Form`) and
  // every one of them renders this footer, so this is the one place a failed
  // save can be shown for all of them. rc-form puts the message on form state
  // and renders it nowhere unless a consumer asks: before this, a save that
  // 500'd left the modal open with the typed value still in the field and no
  // error anywhere, which looks exactly like a save that worked.
  const { errors } = useInternalForm();

  return (
    <footer>
      {item && (
        <ButtonStyled
          negative
          outline
          left="0"
          right="2"
          type="button"
          withIcon="sm"
          onClick={() => {
            removeItem(item.id);
            if (closeFn) closeFn();
            posthog?.capture('deleteItem');
          }}
        >
          <Icon name="ACTIONS_Delete" size="20" />
          Delete
        </ButtonStyled>
      )}
      {children}
      {!!errors.length && (
        // The footer is sticky, so this stays on screen however far the modal
        // body is scrolled. rc-form's own text ("Something has gone wrong,
        // please try again.") never says the edit did not land, so say it.
        <div
          css={theme => [
            theme.font('xs'),
            { color: theme.colors.highlight, marginRight: 'auto' },
          ]}
          data-e2e="save-error"
        >
          <strong>Not saved.</strong>
          <ErrorMsgsStyled />
        </div>
      )}
      {/* rc-form's SubmitButton: a spinner while the save is in flight, a red
          X when it fails, and no clicks accepted in between. */}
      <SubmitButtonStyled
        left={children ? '2' : undefined}
        right="0"
        onClick={closeFn}
      >
        Save
      </SubmitButtonStyled>
    </footer>
  );
}
